import type {
    Filter,
    GetDropdownValuesRequest,
} from "@amzn/d16-g-client-deck-builder-typescript-client";

import { getCdbApiClient } from "../../../api/cdbApi/cdbApi";
import { CLIENT_ID } from "../../../config/constants";
import type { InputParameterFilter } from "../../../types/common";
import { getDropdownFilters } from "../DropdownDimensionHelpers";

export enum ListBuilderValidatorType {
    REGEX = "regex",
    CDB_INPUTS = "cdb_inputs",
}

export const ListBuilderValidationAsyncType: {
    [key in ListBuilderValidatorType]: boolean;
} = {
    [ListBuilderValidatorType.REGEX]: false,
    [ListBuilderValidatorType.CDB_INPUTS]: true,
};

export interface ListBuilderRegexValidator {
    type: ListBuilderValidatorType.REGEX;
    regex: string;
}

export interface ListBuilderCDBInputValidator {
    type: ListBuilderValidatorType.CDB_INPUTS;
    inputSource: string;
    column: string;
    inputParameterFilters?: InputParameterFilter[];
    isAdvertiserScoped?: boolean;
    domainIdentifier?: string;
    dimensionIdentifier?: string;
}

export type ListBuilderValidators =
    | ListBuilderRegexValidator
    | ListBuilderCDBInputValidator;

export interface ValidatorOutput {
    validItems: string[];
    invalidItems: string[];
}

export type ListItemValidationHandler<T extends ListBuilderValidators> = (
    item: string,
    validatorConfiguration: T,
) => Promise<boolean>;

export const regexItemValidator: ListItemValidationHandler<
    ListBuilderRegexValidator
> = async (_item, _validatorConfiguration) => {
    throw new Error("Not implemented");
};

export const cdbInputsItemValidator: ListItemValidationHandler<
    ListBuilderCDBInputValidator
> = async (item, config) => {
    const client = await getCdbApiClient();

    const itemFilter: Filter = {
        key: config.column,
        value: item,
        comparator: "EQUALS",
    };

    const filters =
        getDropdownFilters(
            config.inputParameterFilters,
            undefined,
            config.isAdvertiserScoped,
        ) ?? [];

    const request: GetDropdownValuesRequest = {
        datasetName: config.inputSource,
        columns: [
            {
                column: config.column,
            },
        ],
        pageSize: 5,
        additionalFilters: [...filters, itemFilter],
        metadata: {
            dimensionIdentifier: config.dimensionIdentifier ?? "list-builder",
            domainIdentifier: config.domainIdentifier,
            clientId: CLIENT_ID,
        },
    };

    const { maxResults } = await client.getDropdownValues(request);
    const result = Boolean(maxResults && maxResults >= 1);
    return result;
};

export const validateItem: ListItemValidationHandler<
    ListBuilderValidators
> = async (item, validatorConfiguration) => {
    switch (validatorConfiguration.type) {
        case ListBuilderValidatorType.REGEX:
            return regexItemValidator(item, validatorConfiguration);
        case ListBuilderValidatorType.CDB_INPUTS:
            return cdbInputsItemValidator(item, validatorConfiguration);
        default:
            throw new Error("Invalid validator type");
    }
};
